import React, { useCallback, useMemo, useState } from 'react';

import { Box, Container, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import LiveRegionStatus from 'components/LiveRegionStatus';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import { DEFAULT_OWNER_WORKSPACE_FILTERS } from 'config/api/assetRopa/assetRopaFilter.types';
import { isAvailableProcessingActivityGoneError, ownerWorkspaceFiltersAreActive } from 'config/api/assetRopa/assetRopaWorkspace.helpers';
import type { OwnerWorkspaceAsset } from 'config/api/assetRopa/assetRopaWorkspace.types';
import type { AssetProcessingActivityReviewRow } from 'config/api/ropaReviews/ropaReviews.types';
import usePageTitle from 'hooks/usePageTitle';
import useSessionFilters from 'hooks/useSessionFilters';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import DpiaHistoryDialog from 'pages/ProcessingActivities/_shared/DpiaHistoryDialog';
import DpiaReassessDialog from 'pages/ProcessingActivities/_shared/DpiaReassessDialog';
import useDpiaReassess from 'pages/ProcessingActivities/_shared/useDpiaReassess';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import ActiveRopaReviewPanelSection from '../ProcessingActivitiesAssetReviewsPage/_components/ActiveRopaReviewPanelSection';
import AssetRopaMetricsSection from '../ProcessingActivitiesAssetReviewsPage/_components/AssetRopaMetricsSection';
import useStyles from '../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import AddProcessingActivityModal from './_components/AddProcessingActivityModal';
import OwnerWorkspaceAssetPanel from './_components/OwnerWorkspaceAssetPanel';
import OwnerWorkspaceFiltersToolbar from './_components/OwnerWorkspaceFiltersToolbar';
import OwnerWorkspaceIntroPanel from './_components/OwnerWorkspaceIntroPanel';
import OwnerWorkspaceMetricsSection from './_components/OwnerWorkspaceMetricsSection';
import SoftDeleteProcessingActivityModal from './_components/SoftDeleteProcessingActivityModal';
import useOwnerAssetRopaWorkspace, { OWNER_WORKSPACE_SESSION_FILTERS_KEY } from './useOwnerAssetRopaWorkspace';

const OWNER_REVIEW_ACTIONS = {
  canEditDetails: false,
  canCompleteReview: false,
  canCancelReview: false,
};

const OwnerAssetRopaWorkspacePage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const messages = processing_activities_messages.asset_ropa.owner_workspace;
  const emptyMessages = messages.empty_state;

  usePageTitle(t(messages.page_title));

  const [filters, setFilters] = useSessionFilters(OWNER_WORKSPACE_SESSION_FILTERS_KEY, DEFAULT_OWNER_WORKSPACE_FILTERS);
  const [addTarget, setAddTarget] = useState<OwnerWorkspaceAsset | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    asset: OwnerWorkspaceAsset;
    row: AssetProcessingActivityReviewRow;
  } | null>(null);
  const [historyTarget, setHistoryTarget] = useState<AssetProcessingActivityReviewRow | null>(null);
  const [reassessTarget, setReassessTarget] = useState<AssetProcessingActivityReviewRow | null>(null);
  const reassessMutation = useDpiaReassess(organizationId);

  const {
    activeReview,
    addProcessingActivityMutation,
    assets,
    count,
    filterOptions,
    hasNextPage,
    hasPreviousPage,
    isError,
    isForbidden,
    isLoading,
    page,
    pageSize,
    refetch,
    reviewMetrics,
    setPage,
    softDeleteMutation,
    workspaceMetrics,
  } = useOwnerAssetRopaWorkspace({ filters, organizationId });

  const hasActiveFilters = ownerWorkspaceFiltersAreActive(filters);
  const isOrgWideEmpty = !isLoading && !isError && !isForbidden && assets.length === 0 && !hasActiveFilters;
  const isFilteredEmpty = !isLoading && !isError && !isForbidden && assets.length === 0 && hasActiveFilters;

  const searchStatusMessage = useMemo(() => {
    if (isForbidden) {
      return '';
    }

    if (isLoading) {
      return t(processing_activities_messages.asset_ropa.search_status.loading);
    }

    if (isOrgWideEmpty) {
      return `${t(emptyMessages.org_wide_title)}. ${t(emptyMessages.org_wide_body)}`;
    }

    if (isFilteredEmpty) {
      return `${t(emptyMessages.filtered_title)}. ${t(emptyMessages.filtered_body)}`;
    }

    const displayedCount = count > assets.length ? count : assets.length;
    return t(processing_activities_messages.asset_ropa.search_status.owner_assets_count, { count: displayedCount });
  }, [assets.length, count, emptyMessages, isFilteredEmpty, isForbidden, isLoading, isOrgWideEmpty, t]);

  const activeReviewPanel = useMemo(() => {
    if (!activeReview) {
      return null;
    }

    return {
      id: activeReview.id,
      status: activeReview.status,
      initiatedAt: activeReview.initiatedAt,
      deadline: activeReview.deadline,
      reviewInstructions: activeReview.reviewInstructions,
      initiatedByMembershipId: activeReview.initiatedByMembershipId,
      initiatedByDisplayName: activeReview.initiatedByDisplayName,
    };
  }, [activeReview]);

  const handleOpenEditor = useCallback(
    (row: AssetProcessingActivityReviewRow, asset: OwnerWorkspaceAsset, mode: 'view' | 'edit') => {
      history.push(
        generatePath(PATHS.PROCESSING_ACTIVITIES_ASSET_ROPA_APA, {
          assetRopaId: String(asset.assetRopaId),
          apaId: String(row.assetProcessingActivityId),
        }),
        { mode },
      );
    },
    [history],
  );

  const handleOpenDpia = useCallback(
    (row: AssetProcessingActivityReviewRow) => {
      if (!row.dpiaId) return;
      history.push(generatePath(PATHS.PROCESSING_ACTIVITIES_OWNER_DPIA, { dpiaId: String(row.dpiaId) }));
    },
    [history],
  );

  const handleOpenHistoryItem = useCallback(
    (item: { id: number }) => {
      setHistoryTarget(null);
      history.push(generatePath(PATHS.PROCESSING_ACTIVITIES_OWNER_DPIA, { dpiaId: String(item.id) }));
    },
    [history],
  );

  const handleReassessConfirm = useCallback(
    async (reason: string) => {
      if (!reassessTarget) return;

      try {
        const currentId = await reassessMutation.mutateAsync({
          apaId: reassessTarget.assetProcessingActivityId,
          reason,
        });
        enqueueSnackbar(t(messages.toasts.reassessed), { variant: 'success' });
        setReassessTarget(null);
        history.push(generatePath(PATHS.PROCESSING_ACTIVITIES_OWNER_DPIA, { dpiaId: String(currentId) }));
      } catch {
        enqueueSnackbar(t(messages.toasts.reassess_failed), { variant: 'error' });
      }
    },
    [enqueueSnackbar, history, messages.toasts, reassessMutation, reassessTarget, t],
  );

  const assetListContent = useMemo(() => {
    if (isOrgWideEmpty) {
      return (
        <Box className={styles.ownerWorkspaceEmptyState}>
          <Typography gutterBottom variant='h6'>
            {t(emptyMessages.org_wide_title)}
          </Typography>
          <Typography color='textSecondary' variant='body2'>
            {t(emptyMessages.org_wide_body)}
          </Typography>
        </Box>
      );
    }

    if (isFilteredEmpty) {
      return (
        <Box className={styles.ownerWorkspaceEmptyState}>
          <Typography gutterBottom variant='h6'>
            {t(emptyMessages.filtered_title)}
          </Typography>
          <Typography color='textSecondary' variant='body2'>
            {t(emptyMessages.filtered_body)}
          </Typography>
          <ColoredButton
            className=''
            customColor='secondary'
            disabled={false}
            icon={false}
            onClick={() => setFilters(DEFAULT_OWNER_WORKSPACE_FILTERS)}
            showLoader={false}
            style={{ marginTop: 16 }}
            textLike={false}
            variant='outlined'
          >
            {t(emptyMessages.clear_filters)}
          </ColoredButton>
        </Box>
      );
    }

    return (
      <section aria-label={t(messages.page_title)} className={styles.assetList}>
        {assets.map(asset => (
          <OwnerWorkspaceAssetPanel
            key={asset.id}
            asset={asset}
            hasActiveReview={Boolean(activeReview)}
            onAddProcessingActivity={setAddTarget}
            onOpenDpia={handleOpenDpia}
            onOpenEditor={handleOpenEditor}
            onRemoveProcessingActivity={(row, assetItem) => setDeleteTarget({ asset: assetItem, row })}
            onStartReassessment={setReassessTarget}
            onViewDpiaHistory={setHistoryTarget}
            organizationId={organizationId}
          />
        ))}
        {(hasPreviousPage || hasNextPage) && (
          <Box display='flex' justifyContent='space-between' marginTop={2}>
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={!hasPreviousPage}
              icon={false}
              onClick={() => setPage(page - 1)}
              showLoader={false}
              textLike={false}
              variant='outlined'
            >
              {t(general_messages.previous)}
            </ColoredButton>
            <Typography color='textSecondary' variant='body2'>
              {t(processing_activities_messages.asset_ropa.owner_workspace.pagination_status, {
                from: (page - 1) * pageSize + 1,
                to: Math.min(page * pageSize, count),
                total: count,
              })}
            </Typography>
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={!hasNextPage}
              icon={false}
              onClick={() => setPage(page + 1)}
              showLoader={false}
              textLike={false}
              variant='outlined'
            >
              {t(general_messages.next)}
            </ColoredButton>
          </Box>
        )}
      </section>
    );
  }, [
    assets,
    count,
    emptyMessages,
    hasNextPage,
    hasPreviousPage,
    page,
    pageSize,
    setPage,
    handleOpenDpia,
    handleOpenEditor,
    isFilteredEmpty,
    isOrgWideEmpty,
    messages.page_title,
    organizationId,
    activeReview,
    setFilters,
    styles.assetList,
    styles.ownerWorkspaceEmptyState,
    t,
  ]);

  const handleAddConfirm = useCallback(
    async (processingActivityId: number) => {
      if (!addTarget) return 'failed';

      try {
        await addProcessingActivityMutation.mutateAsync({
          assetRopaId: addTarget.assetRopaId,
          payload: { processingActivityId },
        });
        enqueueSnackbar(t(messages.toasts.added), { variant: 'success' });
        setAddTarget(null);
        return 'success' as const;
      } catch (error) {
        if (isAvailableProcessingActivityGoneError(error)) {
          return 'unavailable' as const;
        }

        enqueueSnackbar(t(messages.toasts.add_failed), { variant: 'error' });
        return 'failed' as const;
      }
    },
    [addProcessingActivityMutation, addTarget, enqueueSnackbar, messages, t],
  );

  const handleDeleteConfirm = useCallback(
    async (reason: string) => {
      if (!deleteTarget) return;

      try {
        await softDeleteMutation.mutateAsync({
          apaId: deleteTarget.row.assetProcessingActivityId,
          assetRopaId: deleteTarget.asset.assetRopaId,
          payload: { deletionReason: reason },
        });
        enqueueSnackbar(t(messages.toasts.removed), { variant: 'success' });
        setDeleteTarget(null);
      } catch {
        enqueueSnackbar(t(messages.toasts.remove_failed), { variant: 'error' });
      }
    },
    [deleteTarget, enqueueSnackbar, messages, softDeleteMutation, t],
  );

  let content: React.ReactNode;

  if (isForbidden) {
    content = (
      <Box className={styles.forbiddenState}>
        <Typography gutterBottom variant='h6'>
          {t(processing_activities_messages.asset_ropa.forbidden_title)}
        </Typography>
        <Typography color='textSecondary' variant='body2'>
          {t(processing_activities_messages.asset_ropa.owner_forbidden_body)}
        </Typography>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={false}
          icon={false}
          onClick={() => history.push(PATHS.ROOT)}
          showLoader={false}
          style={{ marginTop: 16 }}
          textLike={false}
          variant='contained'
        >
          {t(processing_activities_messages.forbidden_back)}
        </ColoredButton>
      </Box>
    );
  } else if (isLoading) {
    content = <Loader inner />;
  } else if (isError) {
    content = (
      <Box className={styles.errorBanner}>
        <Typography color='error' gutterBottom variant='body2'>
          {t(processing_activities_messages.asset_ropa.errors.load_failed)}
        </Typography>
        <TextButton onClick={() => refetch()}>{t(general_messages.retry)}</TextButton>
      </Box>
    );
  } else {
    content = (
      <>
        {activeReviewPanel && (
          <ActiveRopaReviewPanelSection
            actions={OWNER_REVIEW_ACTIONS}
            onCancelReview={() => undefined}
            onCompleteReview={() => undefined}
            onEditReview={() => undefined}
            review={activeReviewPanel}
            showAdminActions={false}
          />
        )}
        {reviewMetrics && <AssetRopaMetricsSection includedAssetsMessageKey='owner_included_assets' metrics={reviewMetrics} />}
        <OwnerWorkspaceIntroPanel />
        <OwnerWorkspaceMetricsSection metrics={workspaceMetrics} />
        <OwnerWorkspaceFiltersToolbar filterOptions={filterOptions} filters={filters} onFiltersChange={setFilters} />
        {assetListContent}
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader backButton noBorder onBack={() => history.push(PATHS.PROCESSING_ACTIVITIES)} subtitle={t(messages.page_subtitle)}>
          {t(messages.page_title)}
        </ManagePageHeader>
        <LiveRegionStatus message={searchStatusMessage} />
        {content}
        <AddProcessingActivityModal
          assetRopaId={addTarget?.assetRopaId ?? 0}
          loading={addProcessingActivityMutation.isLoading}
          onClose={() => setAddTarget(null)}
          onConfirm={handleAddConfirm}
          open={Boolean(addTarget)}
          organizationId={organizationId}
        />
        <SoftDeleteProcessingActivityModal
          loading={softDeleteMutation.isLoading}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          open={Boolean(deleteTarget)}
          processingActivityName={deleteTarget?.row.processingActivityName}
        />
        <DpiaHistoryDialog
          apaId={historyTarget?.assetProcessingActivityId}
          onClose={() => setHistoryTarget(null)}
          onOpenItem={handleOpenHistoryItem}
          open={Boolean(historyTarget)}
          organizationId={organizationId}
        />
        <DpiaReassessDialog
          loading={reassessMutation.isLoading}
          onClose={() => setReassessTarget(null)}
          onConfirm={handleReassessConfirm}
          open={Boolean(reassessTarget)}
        />
      </Container>
    </ErrorBoundary>
  );
};

export default OwnerAssetRopaWorkspacePage;
